// hooks/useTimetable.js (UPDATED with term support and conflict handling)

import { useState, useCallback, useMemo, useEffect } from 'react';
import api from '../../../api';

/**
 * Fetches faculty name by userId using /api/users/:userId
 */
export const fetchFacultyNameById = async (userId, token) => {
  if (!userId) return '';
  try {
    const res = await api.get(`/api/users/${userId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });
    const user = res.data;
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || user.name || user.fullName || '';
  } catch (error) {
    console.error(`[fetchFacultyNameById] Error fetching faculty ${userId}:`, error);
    return '';
  }
}

/**
 * Build comprehensive faculty map from multiple data sources
 */
export const buildFacultyMap = (facultyData = [], subjects = [], classFacultyAssignments = []) => {
  console.log('[buildFacultyMap] Building faculty map from sources');
  const facultyMap = new Map();
  
  // 1. Seed from facultyData (user-like objects)
  facultyData.forEach(faculty => {
    const id = faculty?.userId || faculty?._id || faculty?.staffId || faculty?.id;
    if (id) {
      const firstName = faculty?.firstName || faculty?.name || (faculty?.user && (faculty.user.firstName || faculty.user.name)) || '';
      const lastName = faculty?.lastName || faculty?.surname || (faculty?.user && faculty.user.lastName) || '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      if (fullName) facultyMap.set(String(id), fullName);
    }
  });

  // 2. Add from subjects.assignedFaculty
  subjects.forEach(subject => {
    const assignedFaculty = subject?.assignedFaculty || subject?.assigned_faculty || subject?.facultyAssigned || [];
    if (Array.isArray(assignedFaculty)) {
      assignedFaculty.forEach(faculty => {
        const facultyId = faculty?.userId || faculty?._id || faculty?.staffId || faculty?.id;
        if (facultyId) {
          const firstName = faculty?.firstName || faculty?.name || (faculty?.user && (faculty.user.firstName || faculty.user.name)) || '';
          const lastName = faculty?.lastName || faculty?.surname || (faculty?.user && faculty.user.lastName) || '';
          const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
          if (fullName && !facultyMap.has(String(facultyId))) {
            facultyMap.set(String(facultyId), fullName);
          }
        }
      });
    }
  });

  // 3. Add from classFacultyAssignments
  classFacultyAssignments.forEach(assignment => {
    const id = assignment?.facultyId || assignment?._id || assignment?.id;
    if (id) {
      const firstName = assignment?.firstName || assignment?.name || '';
      const lastName = assignment?.lastName || assignment?.surname || '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      if (fullName && !facultyMap.has(String(id))) {
        facultyMap.set(String(id), fullName);
      }
    }
  });

  console.log(`[buildFacultyMap] Built map with ${facultyMap.size} faculty entries`);
  return facultyMap;
};

/**
 * Format faculty name from object
 */
export const formatFacultyName = (faculty) => {
  if (!faculty) return null;
  
  if (typeof faculty === 'string' || typeof faculty === 'number') {
    return null;
  }
  
  const firstName = faculty?.firstName || faculty?.name || (faculty?.user && (faculty.user.firstName || faculty.user.name)) || '';
  const lastName = faculty?.lastName || faculty?.surname || (faculty?.user && faculty.user.lastName) || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  
  return fullName || null;
};

/**
 * Resolve faculty name using faculty map
 */
export const resolveFacultyName = (facultyIdOrObj, facultyMap) => {
  if (!facultyIdOrObj) return null;
  
  if (typeof facultyIdOrObj === 'object') {
    const name = formatFacultyName(facultyIdOrObj);
    if (name) return name;
    facultyIdOrObj = facultyIdOrObj.userId || facultyIdOrObj._id || facultyIdOrObj.staffId || facultyIdOrObj.id;
  }
  
  return facultyMap.get(String(facultyIdOrObj)) || null;
};

/**
 * Aggregate faculty names for elective selections
 */
export const aggregateFacultyForSelection = (selection, facultyMap) => {
  if (!selection) return null;
  const names = new Set();
  
  if (selection.facultyId) {
    const n = resolveFacultyName(selection.facultyId, facultyMap);
    if (n) names.add(n);
    if (selection.facultyName) names.add(selection.facultyName);
  } else if (selection.facultyName) {
    names.add(selection.facultyName);
  }
  
  if (Array.isArray(selection.subgroupSelections) && selection.subgroupSelections.length) {
    selection.subgroupSelections.forEach(sg => {
      const n = resolveFacultyName(sg.facultyId, facultyMap) || sg.facultyName || null;
      if (n) names.add(n);
    });
  }
  
  return Array.from(names).join(', ') || null;
};

/**
 * Extract all faculty IDs from timetable entries
 */
export const extractFacultyIdsFromTimetable = (timetableData) => {
  const facultyIds = new Set();
  
  if (!timetableData) return facultyIds;
  
  const entries = Array.isArray(timetableData) 
    ? timetableData 
    : (timetableData.entries || []);
  
  entries.forEach(entry => {
    if (entry.facultyUserId) facultyIds.add(String(entry.facultyUserId));
    if (entry.facultyId) facultyIds.add(String(entry.facultyId));
    
    const electiveSelections = entry.electiveSelections || entry.electives || [];
    electiveSelections.forEach(selection => {
      if (selection.facultyId) facultyIds.add(String(selection.facultyId));
      if (selection.facultyUserId) facultyIds.add(String(selection.facultyUserId));
      
      const subgroupSelections = selection.subgroupSelections || [];
      subgroupSelections.forEach(subgroup => {
        if (subgroup.facultyId) facultyIds.add(String(subgroup.facultyId));
      });
    });
  });
  
  return Array.from(facultyIds);
};

// Main hook
export const useTimetable = (selectedClassId, selectedAcademicYear, selectedSemester, selectedTerm, isSemesterBased, tenantId, token) => {
  const [weekTemplate, setWeekTemplate] = useState([]);
  const [baseWeekDocId, setBaseWeekDocId] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [labBatches, setLabBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState('');
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [additionalFacultyData, setAdditionalFacultyData] = useState([]);
  const [pendingConflicts, setPendingConflicts] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  console.log('[useTimetable] Hook initialized with:', {
    selectedClassId,
    selectedAcademicYear,
    selectedSemester,
    selectedTerm,
    isSemesterBased,
    tenantId,
    token: !!token
  });

  // Build comprehensive faculty map
  const facultyMap = useMemo(() => 
    buildFacultyMap(additionalFacultyData, subjects, []),
    [additionalFacultyData, subjects]
  );

  // Fetch missing faculty data
  const fetchMissingFaculty = useCallback(async (facultyIds) => {
    if (!facultyIds.length || !token) return [];
    
    console.log(`[fetchMissingFaculty] Fetching ${facultyIds.length} faculty`);
    
    try {
      const facultyPromises = facultyIds.map(async (facultyId) => {
        try {
          const res = await api.get(`/api/users/${facultyId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return res.data;
        } catch {
          return null;
        }
      });
      
      const facultyResults = await Promise.all(facultyPromises);
      const validResults = facultyResults.filter(Boolean);
      console.log(`[fetchMissingFaculty] Fetched ${validResults.length} faculty successfully`);
      return validResults;
    } catch (error) {
      console.error('[fetchMissingFaculty] Error:', error);
      return [];
    }
  }, [token]);

  // Fetch subjects
  useEffect(() => {
    async function fetchSubjects() {
      if (!selectedClassId || !tenantId) {
        console.log('[fetchSubjects] Missing selectedClassId or tenantId');
        return;
      }
      
      console.log(`[fetchSubjects] Fetching subjects for class ${selectedClassId}`);
      
      try {
        const res = await api.get(`/api/classManager/subjects-for-class/${selectedClassId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        
        const subjectsArr = res.data.subjects || [];
        console.log(`[fetchSubjects] Found ${subjectsArr.length} subjects`);
        
        const subjectsWithFaculty = await Promise.all(
          subjectsArr.map(async (subject) => {
            try {
              const facultyRes = await api.get(
                `/api/subjects/get-faculty-by-class-and-subject/${selectedClassId}/${subject.id}?tenantId=${tenantId}`,
                { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
              );
              const facultyList = facultyRes.data.faculty || [];
              return {
                ...subject,
                assignedFaculty: facultyList,
              };
            } catch (err) {
              console.warn(`[fetchSubjects] No faculty for subject ${subject.id}`);
              return {
                ...subject,
                assignedFaculty: [],
              };
            }
          })
        );
        
        setSubjects(subjectsWithFaculty);
      } catch (err) {
        console.error('[fetchSubjects] Error:', err);
        setSubjects([]);
      }
    }
    
    fetchSubjects();
  }, [selectedClassId, tenantId, token]);

  // Fetch lab batches
  useEffect(() => {
    async function fetchLabBatches() {
      if (!selectedClassId) {
        setLabBatches([]);
        return;
      }
      
      console.log(`[fetchLabBatches] Fetching lab batches for class ${selectedClassId}`);
      
      try {
        const res = await api.get(`/api/classManager/lab-batches/${selectedClassId}`);
        console.log('[fetchLabBatches] Response:', res.data);
        setLabBatches(res.data.batches || []);
      } catch (err) {
        console.error('[fetchLabBatches] Error:', err);
        setLabBatches([]);
      }
    }
    
    fetchLabBatches();
  }, [selectedClassId]);

  // Fetch base week
  const fetchBaseWeek = useCallback(async () => {
    console.log('[fetchBaseWeek] Called');
    
    try {
      setIsLoading(true);
      
      const params = new URLSearchParams({
        classId: selectedClassId,
        academicYearId: selectedAcademicYear
      });
      
      if (isSemesterBased) {
        params.append('semesterType', selectedSemester || '');
        console.log('[fetchBaseWeek] Using semesterType:', selectedSemester);
      } else {
        params.append('termId', selectedTerm?._id || '');
        console.log('[fetchBaseWeek] Using termId:', selectedTerm?._id);
      }
      
      console.log('[fetchBaseWeek] Params:', params.toString());
      
      const res = await api.get(`/api/timetable/base-week?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      console.log('[fetchBaseWeek] Response:', res.data);

      if (res?.data && res.data.success && res.data.data) {
        const timetableData = res.data.data;
        setWeekTemplate(timetableData);
        setBaseWeekDocId(res.data.id || null);
        setError('');
        
        // Extract and fetch missing faculty data
        const facultyIds = extractFacultyIdsFromTimetable(timetableData);
        if (facultyIds.length) {
          console.log('[fetchBaseWeek] Fetching missing faculty data for IDs:', facultyIds);
          const missingFaculty = await fetchMissingFaculty(facultyIds);
          setAdditionalFacultyData(prev => [...prev, ...missingFaculty]);
        }
      } else {
        console.log('[fetchBaseWeek] No timetable found');
        setWeekTemplate([]);
        setBaseWeekDocId(null);
        setError('No timetable found for this class');
      }

      return res?.data || null;
    } catch (err) {
      console.error('[fetchBaseWeek] Error:', err);
      setWeekTemplate([]);
      setBaseWeekDocId(null);
      setError(err?.response?.data?.message || 'Error fetching timetable');
      return { error: err };
    } finally {
      setIsLoading(false);
    }
  }, [selectedClassId, selectedAcademicYear, selectedSemester, selectedTerm, isSemesterBased, token, fetchMissingFaculty]);

  // Check for conflicts before saving
  const checkForConflicts = useCallback(async (weekTemplateData) => {
    console.log('[checkForConflicts] Checking for conflicts');
    
    try {
      const conflictChecks = [];
      
      // Check each entry for faculty conflicts
      weekTemplateData.forEach((entry, index) => {
        if (entry.facultyUserId) {
          conflictChecks.push(
            api.post('/api/timetable/check-faculty-conflict', {
              tenantId,
              facultyUserId: entry.facultyUserId,
              dayOfWeek: entry.dayOfWeek,
              timeSlot: entry.timeSlot,
              excludeClassId: selectedClassId,
              academicYearId: selectedAcademicYear,
              termId: isSemesterBased ? null : selectedTerm?._id,
              semesterType: isSemesterBased ? selectedSemester : null
            }, {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined
            })
          );
        }
      });
      
      const results = await Promise.all(conflictChecks);
      const conflicts = results
        .map(res => res.data)
        .filter(data => data.conflict)
        .flatMap(data => data.conflicts || []);
      
      console.log('[checkForConflicts] Found conflicts:', conflicts);
      return conflicts;
    } catch (error) {
      console.error('[checkForConflicts] Error:', error);
      return [];
    }
  }, [tenantId, selectedClassId, selectedAcademicYear, selectedSemester, selectedTerm, isSemesterBased, token]);

  // Save timetable with conflict handling
  const saveTimetable = useCallback(async (approvedConflicts = []) => {
    console.log('[saveTimetable] Called with approvedConflicts:', approvedConflicts);
    
    if (!selectedClassId || !tenantId || !selectedAcademicYear || (isSemesterBased && !selectedSemester) || (!isSemesterBased && !selectedTerm)) {
      const msg = 'Please select academic year, semester/term, and class first';
      console.error('[saveTimetable]', msg);
      return msg;
    }

    setLoadingButton('save');
    
    try {
      // Prepare week template
      const sanitizedWeekTemplate = weekTemplate.map(entry => {
        if (!entry.facultyUserId && entry.subjectId) {
          const subject = subjects.find(
            s => String(s.id) === String(entry.subjectId) || String(s._id) === String(entry.subjectId)
          );
          const faculty = subject?.assignedFaculty?.[0];
          return {
            ...entry,
            facultyUserId: faculty?._id || '',
          };
        }
        return entry;
      });

      // Check for conflicts if not already approved
      if (approvedConflicts.length === 0) {
        const conflicts = await checkForConflicts(sanitizedWeekTemplate);
        
        if (conflicts.length > 0) {
          console.log('[saveTimetable] Conflicts detected, prompting user');
          setPendingConflicts(conflicts);
          setShowConflictModal(true);
          setLoadingButton('');
          return 'conflicts_detected';
        }
      }

      // Prepare payload with term/semester info
      const payload = {
        tenantId,
        classId: selectedClassId,
        academicYearId: selectedAcademicYear,
        weekTemplate: sanitizedWeekTemplate
      };

      if (isSemesterBased) {
        payload.semesterType = selectedSemester;
        console.log('[saveTimetable] Adding semesterType:', selectedSemester);
      } else {
        payload.termId = selectedTerm?._id;
        payload.termNumber = selectedTerm?.termNumber;
        payload.termName = selectedTerm?.termLabel || selectedTerm?.termName;
        console.log('[saveTimetable] Adding term info:', {
          termId: selectedTerm?._id,
          termNumber: selectedTerm?.termNumber,
          termName: selectedTerm?.termLabel
        });
      }

      // If we have approved conflicts, add them to payload
      if (approvedConflicts.length > 0) {
        payload.approvedConflicts = approvedConflicts;
        console.log('[saveTimetable] Including approved conflicts:', approvedConflicts);
      }

      let response;
      if (baseWeekDocId) {
        console.log('[saveTimetable] Updating existing timetable:', baseWeekDocId);
        response = await api.put('/api/timetable/base-week', {
          docId: baseWeekDocId,
          ...payload
        });
      } else {
        console.log('[saveTimetable] Creating new timetable');
        response = await api.post('/api/timetable/base-week', payload);
      }

      console.log('[saveTimetable] Save response:', response.data);

      if (response.data.success) {
        setSaveStatus('Timetable saved successfully!');
        setError('');
        setPendingConflicts([]);
        setShowConflictModal(false);
        await fetchBaseWeek(); // Refresh
        return null;
      } else if (response.data.requiresConfirmation) {
        console.log('[saveTimetable] Server requires conflict confirmation');
        setPendingConflicts(response.data.conflicts || []);
        setShowConflictModal(true);
        setLoadingButton('');
        return 'conflicts_detected';
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      console.error('[saveTimetable] Error:', err);
      
      // Check if error contains conflict info
      if (err.response?.status === 409) {
        console.log('[saveTimetable] Conflict error with details:', err.response.data);
        setPendingConflicts(err.response.data.conflicts || []);
        setShowConflictModal(true);
        setLoadingButton('');
        return 'conflicts_detected';
      }
      
      setSaveStatus('');
      return err.response?.data?.message || 'Error saving timetable';
    } finally {
      if (pendingConflicts.length === 0) {
        setLoadingButton('');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    }
  }, [selectedClassId, tenantId, selectedAcademicYear, isSemesterBased, selectedSemester, selectedTerm, weekTemplate, subjects, checkForConflicts, setPendingConflicts, setShowConflictModal, setLoadingButton, baseWeekDocId, fetchBaseWeek, pendingConflicts]);

  // Approve conflicts and save
  const approveConflictsAndSave = useCallback(async (reason = '') => {
    console.log('[approveConflictsAndSave] Approving conflicts with reason:', reason);
    
    if (pendingConflicts.length === 0) {
      console.log('[approveConflictsAndSave] No pending conflicts');
      return saveTimetable([]);
    }
    
    try {
      // First, send approval to server
      const approvalResponse = await api.post('/api/timetable/approve-conflicts', {
        conflicts: pendingConflicts,
        approvedBy: localStorage.getItem('userId'),
        reason,
        timetableData: {
          tenantId,
          classId: selectedClassId,
          academicYearId: selectedAcademicYear,
          weekTemplate: weekTemplate.map(entry => ({
            ...entry,
            _index: weekTemplate.indexOf(entry)
          }))
        }
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      
      console.log('[approveConflictsAndSave] Approval response:', approvalResponse.data);
      
      // Then save with approved conflicts
      return await saveTimetable(pendingConflicts);
    } catch (error) {
      console.error('[approveConflictsAndSave] Error:', error);
      return error.response?.data?.message || 'Error approving conflicts';
    }
  }, [pendingConflicts, tenantId, selectedClassId, selectedAcademicYear, weekTemplate, token, saveTimetable]);

  // Reject conflicts and cancel
  const rejectConflicts = useCallback(() => {
    console.log('[rejectConflicts] Rejecting conflicts');
    setPendingConflicts([]);
    setShowConflictModal(false);
    setLoadingButton('');
  }, []);

  // Delete timetable
  const deleteTimetable = async () => {
    console.log('[deleteTimetable] Called');
    
    if (!baseWeekDocId || !selectedAcademicYear || !selectedClassId || (isSemesterBased && !selectedSemester)) {
      return 'No timetable to delete or missing selection';
    }

    setLoadingButton('delete');
    
    try {
      let url = `/api/timetable/base-week/${baseWeekDocId}?academicYearId=${selectedAcademicYear}&classId=${selectedClassId}`;
      
      if (isSemesterBased) {
        url += `&semesterType=${selectedSemester}`;
      } else if (selectedTerm) {
        url += `&termId=${selectedTerm._id}`;
      }
      
      console.log('[deleteTimetable] Deleting with URL:', url);
      
      await api.delete(url);
      
      setWeekTemplate([]);
      setBaseWeekDocId(null);
      setSaveStatus('Timetable deleted successfully!');
      setError('');
      return null;
    } catch (err) {
      console.error('[deleteTimetable] Error:', err);
      setSaveStatus('');
      return err.response?.data?.message || 'Error deleting timetable';
    } finally {
      setLoadingButton('');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Delete week template entry
  const deleteWeekTemplateEntry = async (entryId) => {
    console.log('[deleteWeekTemplateEntry] Deleting entry:', entryId);
    
    if (!baseWeekDocId || !entryId) return 'No timetable or entry selected';
    
    setLoadingButton('delete-entry');
    
    try {
      await api.delete(`/api/timetable/base-week/${baseWeekDocId}/week-template/${entryId}`);
      setWeekTemplate(prev => prev.filter(e => String(e._id) !== String(entryId)));
      setSaveStatus('Entry deleted successfully!');
      setError('');
      return null;
    } catch (err) {
      console.error('[deleteWeekTemplateEntry] Error:', err);
      setSaveStatus('');
      return err.response?.data?.message || 'Error deleting entry';
    } finally {
      setLoadingButton('');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  // Handle subject change
  const handleSubjectChange = async (index, subjectId, dayOfWeek, timeSlot) => {
    console.log('[handleSubjectChange]', { index, subjectId, dayOfWeek, timeSlot });
    
    if (!tenantId) {
      setError('Tenant ID is missing. Please reload the page or contact admin.');
      return;
    }

    const updatedTemplate = [...weekTemplate];
    const entry = updatedTemplate[index];

    if (!entry) return;

    entry.subjectId = subjectId;

    try {
      // Check if subject is lab or theory
      let isLab = false;
      try {
        const labCheckRes = await api.get(
          `/api/subjects/subject/${subjectId}/class/${selectedClassId}/is-lab-in-class?tenantId=${tenantId}`
        );
        isLab = labCheckRes.data.isLab && labCheckRes.data.availableInClass;
        console.log('[handleSubjectChange] Lab check result:', isLab);
      } catch {
        isLab = false;
      }

      if (isLab) {
        entry.type = 'Lab';
      } else {
        entry.type = 'Period';
        entry.batchId = '';
      }

      // Fetch faculty for this subject in this class
      const res = await api.get(
        `/api/subjects/get-faculty-by-class-and-subject/${selectedClassId}/${subjectId}?tenantId=${tenantId}`
      );
      const faculty = res.data.faculty?.[0];
      if (faculty) {
        entry.facultyUserId = faculty._id || '';
        entry.facultyName = `${faculty.firstName} ${faculty.lastName}`.trim();
        console.log('[handleSubjectChange] Assigned faculty:', entry.facultyName);
      } else {
        entry.facultyUserId = '';
        entry.facultyName = '';
      }
    } catch (err) {
      console.warn('[handleSubjectChange] Error:', err);
      // ignore, leave faculty blank
    } finally {
      setWeekTemplate(updatedTemplate);
    }
  };

  const handleRemoveEntry = async (index) => {
    const entry = weekTemplate[index];
    if (entry && entry._id) {
      if (window.confirm('Are you sure you want to delete this entry?')) {
        await deleteWeekTemplateEntry(entry._id);
      }
    } else {
      setWeekTemplate(prev => prev.filter((_, i) => i !== index));
    }
  };

  return {
    weekTemplate,
    setWeekTemplate,
    baseWeekDocId,
    subjects,
    labBatches,
    isLoading,
    loadingButton,
    error,
    saveStatus,
    facultyMap,
    pendingConflicts,
    showConflictModal,
    resolveFacultyName: (idOrObj) => resolveFacultyName(idOrObj, facultyMap),
    aggregateFacultyForSelection: (selection) => aggregateFacultyForSelection(selection, facultyMap),
    formatFacultyName,
    fetchBaseWeek,
    saveTimetable,
    deleteTimetable,
    deleteWeekTemplateEntry,
    handleSubjectChange,
    handleRemoveEntry,
    fetchMissingFaculty,
    extractFacultyIdsFromTimetable,
    approveConflictsAndSave,
    rejectConflicts,
    setShowConflictModal
  };
};